// Map a raw server/job error to ONE simple human sentence for the UI. The raw
// message is ALWAYS console-logged by the caller — this is only what the user
// sees, so it must never invent a cause. Image messages require an actual image
// context (a bare number like a commit SHA or a token count must NOT read as
// "image too large"). Unknown → the caller's friendly fallback.
export function friendlyError(raw: unknown, fallback = 'Something went wrong — please try again.'): string {
  const text = raw instanceof Error ? raw.message : String(raw ?? '');
  const msg = text.toLowerCase();
  if (!msg.trim()) return fallback;

  const aboutImage = msg.includes('image') || msg.includes('artboard') || msg.includes('photo');

  // Genuine image-DIMENSION errors only (Anthropic 8000px/side) — must be about an
  // image AND mention a pixel/dimension signal.
  if (aboutImage && (/\bpixels?\b/.test(msg) || msg.includes('dimension') || /\b\d{3,5}\s?px\b/.test(msg))) {
    return 'An image was too large to process — try a smaller one.';
  }
  // Image FILE-SIZE (MB) limit from the upload endpoint.
  if (aboutImage && msg.includes('too large')) {
    return 'That image is over the size limit — re-export it smaller.';
  }
  // Unreadable / wrong-format image (e.g. a media-type mismatch).
  if (aboutImage && (msg.includes('media type') || msg.includes('decode') || msg.includes("wasn’t") || msg.includes("wasn't") || msg.includes('not a usable') || msg.includes('unsupported'))) {
    return 'That image couldn’t be read — try re-saving it as PNG or JPG.';
  }

  if (msg.includes('font must be') || (msg.includes('font') && msg.includes('woff'))) {
    return 'That font wasn’t a .woff2, .woff, .otf or .ttf file.';
  }
  if (msg.includes('live') && msg.includes('protect')) {
    return 'This site is live and protected — changes are turned off.';
  }
  if (msg.includes('timed out') || msg.includes('timeout') || msg.includes('too long')) {
    return 'That took too long — please try again.';
  }
  if (msg.includes('no preview project') || msg.includes('no_project') || msg.includes('pages project')) {
    return 'No preview site is set up yet — ask Neil to finish setup.';
  }
  if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('dispatch failed') || /\bhttp 5\d\d\b/.test(msg)) {
    return 'Couldn’t reach the service — please try again in a moment.';
  }
  if (msg.includes('invalid brand kit')) {
    return 'The brand kit has an invalid value — check the fields and try again.';
  }
  if (msg.includes('no brand kit') || msg.includes('did not match tokenschema')) {
    return 'Couldn’t read a brand kit from those files — try clearer references or different fonts.';
  }

  return fallback;
}
